type CriticalApparatusPreset = {
    App1: boolean;
    App2: boolean;
    App3: boolean;
    App4: boolean;
};

type PresetValues = {
    displayProseVersion?: boolean;
    displayMedievalPunctuation?: boolean;
    displayUncountedVerses?: boolean;
    displayModernPunctuation?: boolean;
    displayOriginalCapitalization?: boolean;
    emphasizeInitials?: boolean;
    characterNormalization?: boolean;
    expandAbbreviations?: boolean;
    displayMisspellings?: boolean;
    displayOriginalLineFlow?: boolean;
    displayOriginalWordBoundaries?: boolean;
    diacriticsModeDisplay?: boolean;
    diacriticsModeHighlight?: boolean;
    displaySuppliedInsert?: boolean;
    displaySuppliedMissingIllegible?: boolean;
    displaySwappedVerses?: boolean;
    displayComments?: boolean;
    highlightPersonNames?: boolean;
    highlightPlaceNames?: boolean;
    highlightSubjectReferences?: boolean;
    displayLabelTitels?: boolean;
    displayCriticalApparatus?: CriticalApparatusPreset;
};

export const TRANSCRIPTION_PRESET: PresetValues = {
    displayProseVersion: false,
    displayMedievalPunctuation: true,
    displayUncountedVerses: true,
    displayModernPunctuation: false,
    displayOriginalCapitalization: true,
    emphasizeInitials: true,
    characterNormalization: false,
    expandAbbreviations: false,
    displayMisspellings: false,
    displayOriginalLineFlow: true,
    displayOriginalWordBoundaries: false,
    diacriticsModeDisplay: false,
    diacriticsModeHighlight: false,
    displaySuppliedInsert: false,
    displaySuppliedMissingIllegible: true,
    displaySwappedVerses: true,
    displayComments: false,
    highlightPersonNames: false,
    highlightPlaceNames: false,
    highlightSubjectReferences: false,
    displayLabelTitels: false,
    displayCriticalApparatus: {
        App1: false,
        App2: false,
        App3: false,
        App4: false,
    },
} as const satisfies PresetValues;

export const TRANSCRIPTION_PLUS_PRESET: PresetValues = {
    ...TRANSCRIPTION_PRESET,
    displayCriticalApparatus: {
        App1: true,
        App2: false,
        App3: true,
        App4: false,
    },
} as const satisfies PresetValues;

export const EDITION_PRESET: PresetValues = {
    displayMedievalPunctuation: false,
    displayUncountedVerses: false,
    displayModernPunctuation: true,
    displayOriginalCapitalization: false,
    emphasizeInitials: false,
    characterNormalization: true,
    expandAbbreviations: true,
    displayMisspellings: true,
    displayOriginalLineFlow: true,
    displayOriginalWordBoundaries: true,
    diacriticsModeDisplay: true,
    displaySuppliedInsert: true,
    displaySuppliedMissingIllegible: true,
    highlightPersonNames: true,
    highlightPlaceNames: true,
    highlightSubjectReferences: true,
    displaySwappedVerses: false,
    displayComments: false,
    displayLabelTitels: true,
    displayCriticalApparatus: {
        App1: false,
        App2: false,
        App3: false,
        App4: false,
    },
} as const satisfies PresetValues;

export const EDITION_PLUS_PRESET: PresetValues = {
    ...EDITION_PRESET,
    displayComments: true,
    displayCriticalApparatus: {
        App1: false,
        App2: true,
        App3: true,
        App4: true,
    },
} as const satisfies PresetValues;

function matchesCriticalApparatus(
    current: CriticalApparatusPreset,
    preset: CriticalApparatusPreset,
): boolean {
    return (
        current.App1 === preset.App1 &&
        current.App2 === preset.App2 &&
        current.App3 === preset.App3 &&
        current.App4 === preset.App4
    );
}

function matchesPreset(
    current: PresetValues,
    preset: PresetValues,
    options?: {
        ignoreCriticalApparatus?: boolean;
        ignoreDisplayComments?: boolean;
    },
): boolean {
    for (const [key, value] of Object.entries(preset) as [
        keyof PresetValues,
        PresetValues[keyof PresetValues],
    ][]) {
        if (key === "displayCriticalApparatus") {
            if (options?.ignoreCriticalApparatus) {
                continue;
            }

            if (!value || !current.displayCriticalApparatus) {
                return false;
            }

            if (
                !matchesCriticalApparatus(
                    current.displayCriticalApparatus,
                    value as CriticalApparatusPreset,
                )
            ) {
                return false;
            }

            continue;
        }

        if (key === "displayComments" && options?.ignoreDisplayComments) {
            continue;
        }

        if (current[key] !== value) {
            return false;
        }
    }

    return true;
}

export function isMatchingPreset(
    textSettings: PresetValues,
    preset: PresetValues,
    options?: {
        ignoreCriticalApparatus?: boolean;
        ignoreDisplayComments?: boolean;
    },
): boolean {
    return matchesPreset(textSettings, preset, options);
}

function applyPreset(textSettings: any, preset: PresetValues) {
    for (const [key, value] of Object.entries(preset) as [
        keyof PresetValues,
        PresetValues[keyof PresetValues],
    ][]) {
        if (key === "displayCriticalApparatus" && value) {
            const apparatus = value as CriticalApparatusPreset;

            textSettings.displayCriticalApparatus.App1 = apparatus.App1;
            textSettings.displayCriticalApparatus.App2 = apparatus.App2;
            textSettings.displayCriticalApparatus.App3 = apparatus.App3;
            textSettings.displayCriticalApparatus.App4 = apparatus.App4;
        } else {
            textSettings[key] = value;
        }
    }
}

export function applyTranscriptionPreset(textSettings: any) {
    applyPreset(textSettings, TRANSCRIPTION_PRESET);
}

export function applyTranscriptionPlusPreset(textSettings: any) {
    applyPreset(textSettings, TRANSCRIPTION_PLUS_PRESET);
}

export function applyEditionPreset(textSettings: any, prose = false) {
    applyPreset(textSettings, {
        ...EDITION_PRESET,
        displayProseVersion: prose,
    });
}

export function applyEditionPlusPreset(textSettings: any, prose = false) {
    applyPreset(textSettings, {
        ...EDITION_PLUS_PRESET,
        displayProseVersion: prose,
    });
}
